import { TextField, Typography } from '@mui/material';
import {
    ChangeEvent,
    createRef,
    FC,
    FormEventHandler,
    Fragment,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Box, Stack } from '@mui/system';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from 'src/components/button';
import { ReactComponent as WarningIcon } from 'src/assets/svg/warning.svg';
import { getTheme } from 'src/utils/get-theme';

import { useRequestOtpSupabase } from '../hooks/use-request-otp-supabase';
import { OtpStep } from '../enum/OtpStep';
import { useVerifyOtpSupabase } from '../hooks/use-verify-otp-supabase';

import { styles } from './styles/otp-form';

const OTP_LENGTH = 6;
const otpBoxes = new Array(OTP_LENGTH).fill(null).map((_, index) => `otp-box-${index}`);
const RESEND_TIMEOUT = 60;

export const SupabaseOtpForm: FC<{
    step: OtpStep;
    goToPreviousStep: () => void;
    goToNextStep: () => void;
    email: string;
    onEmailChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    switchToPassword: () => void;
}> = (props) => {
    const { t } = useTranslation();
    const { step, email, onEmailChange, goToPreviousStep, goToNextStep, switchToPassword } = props;
    const [token, setToken] = useState<string[]>(Array(6).fill(''));
    const inputRefs = useRef(otpBoxes.map(() => createRef<HTMLInputElement>()));
    const { theme } = getTheme();
    const [resendCountdown, setResendCountdown] = useState<number>(0);
    const [lastEmailSent, setLastEmailSent] = useState<string | null>(null);

    const {
        mutate: requestOtp,
        error: otpRequestError,
        isLoading: requestingOtp,
        reset: resetOtpRequest,
    } = useRequestOtpSupabase({
        onSuccess: () => {
            goToNextStep();
            setResendCountdown(RESEND_TIMEOUT);
            setLastEmailSent(email);
        },
    });
    const {
        mutate: verifyOtp,
        error: verifyOtpError,
        isLoading: verifyingOtp,
        reset: resetVerifyOtp,
    } = useVerifyOtpSupabase();

    useEffect(() => {
        if (resendCountdown <= 0) {
            return undefined;
        }

        const interval = setInterval(() => {
            setResendCountdown((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => clearInterval(interval);
    }, [resendCountdown]);

    useEffect(() => {
        if (email !== lastEmailSent) {
            setResendCountdown(0);
        }
    }, [email, lastEmailSent]);

    const getErrorMessage = (): string | null => {
        if (step === OtpStep.EMAIL && otpRequestError) {
            switch (otpRequestError.status) {
                case 422:
                case 403:
                    return t('supabase_otp_login_error_bad_email');
                case 429:
                    return t('supabase_otp_login_error_rate_limit');
                default:
                    return t('supabase_otp_login_error_generic');
            }
        }

        if (step === OtpStep.TOKEN && verifyOtpError) {
            switch (verifyOtpError.status) {
                case 403:
                    return t('supabase_otp_login_error_bad_code');
                default:
                    return t('supabase_otp_login_error_generic');
            }
        }

        if (step === OtpStep.TOKEN && otpRequestError?.status === 429)
            return t('supabase_otp_login_error_rate_limit');

        return null;
    };

    const handleResendCode = () => {
        if (resendCountdown > 0) return;

        setToken(Array(6).fill(''));
        resetVerifyOtp();
        resetOtpRequest();
        requestOtp({ email });
    };

    const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();

        switch (step) {
            case OtpStep.EMAIL:
                requestOtp({ email });
                break;
            case OtpStep.TOKEN:
                verifyOtp({ email, token: token.join('') });
                break;
            default:
        }
    };

    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').trim();

        if (!/^\d+$/.test(pasted)) return;

        const digits = pasted.slice(0, 6).split('');
        const newDigits = ['', '', '', '', '', ''];

        digits.forEach((d, i) => {
            if (i < 6) newDigits[i] = d;
        });

        setToken(newDigits);

        const lastIndex = Math.min(digits.length - 1, 5);

        inputRefs.current[lastIndex].current?.focus();
    };

    return (
        <Box css={styles.login}>
            <Box css={styles.loginContainer}>
                <Stack css={styles.card}>
                    <Box css={styles.headerBox}>
                        <Typography variant="h2" css={styles.headerLineHeight}>
                            {step === OtpStep.EMAIL
                                ? t('supabase_otp_login_title')
                                : t('supabase_otp_login_otp_verify_title')}
                        </Typography>
                        <Typography variant="body2" color={theme.palette.text.secondary}>
                            {step === OtpStep.EMAIL ? (
                                t('supabase_otp_login_subtitle')
                            ) : (
                                <Fragment>
                                    <Trans
                                        i18nKey="supabase_otp_login_otp_verify_subtitle"
                                        values={{ email }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color={theme.palette.text.secondary}
                                        css={styles.marginTop}
                                    >
                                        {t('login_dialog_check_spam')}
                                    </Typography>
                                </Fragment>
                            )}
                        </Typography>
                    </Box>
                    {step === OtpStep.EMAIL && Boolean(otpRequestError) && (
                        <Stack direction="row" alignItems="center" css={styles.errorWrap} gap={1}>
                            <WarningIcon
                                width={theme.typography.h2.fontSize}
                                height={theme.typography.h2.fontSize}
                                color={theme.palette.error.main}
                            />
                            <Typography variant="body1" color={theme.palette.error.main}>
                                {getErrorMessage()}
                            </Typography>
                        </Stack>
                    )}
                    {step === OtpStep.TOKEN &&
                        (Boolean(verifyOtpError) || otpRequestError?.status === 429) && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                css={styles.errorWrap}
                                gap={1}
                            >
                                <WarningIcon
                                    width={theme.typography.h2.fontSize}
                                    height={theme.typography.h2.fontSize}
                                    color={theme.palette.error.main}
                                />
                                <Typography variant="body1" color={theme.palette.error.main}>
                                    {getErrorMessage()}
                                </Typography>
                            </Stack>
                        )}
                    <form onSubmit={handleSubmit}>
                        <Box css={styles.inputsWrap}>
                            {step === OtpStep.EMAIL && (
                                <TextField
                                    value={email}
                                    variant="standard"
                                    onChange={onEmailChange}
                                    type="email"
                                    name="Email"
                                    placeholder={t('supabase_email')}
                                    id="email-input"
                                    css={styles.inputField}
                                    fullWidth
                                />
                            )}
                            {step === OtpStep.TOKEN && (
                                <Stack direction="row" gap="11px">
                                    {otpBoxes.map((id, index) => {
                                        const nextIndex =
                                            index + 1 >= OTP_LENGTH ? index : index + 1;
                                        const previousIndex = index ? index - 1 : index;

                                        const nextRef = inputRefs.current[nextIndex];
                                        const previousRef = inputRefs.current[previousIndex];

                                        const changeHandler = (
                                            e: ChangeEvent<HTMLInputElement>
                                        ) => {
                                            const newToken = [...token];

                                            newToken[index] = e.target.value;

                                            setToken(newToken);

                                            if (!e.target.value) {
                                                previousRef.current?.focus();
                                                return;
                                            }

                                            nextRef.current?.focus();
                                        };
                                        return (
                                            <TextField
                                                key={id}
                                                inputRef={inputRefs.current[index]}
                                                disabled={verifyingOtp || requestingOtp}
                                                value={token[index]}
                                                onChange={changeHandler}
                                                error={Boolean(verifyOtpError)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Backspace') {
                                                        if (!token[index]) {
                                                            e.preventDefault();
                                                            previousRef.current?.focus();
                                                        }
                                                        return;
                                                    }

                                                    if (token[index]) {
                                                        nextRef.current?.focus();
                                                    }
                                                }}
                                                type="text"
                                                inputMode="numeric"
                                                name={`Token-${index}`}
                                                inputProps={{
                                                    style: {
                                                        textAlign: 'center',
                                                    },
                                                    maxLength: 1,
                                                }}
                                                onPaste={handleOtpPaste}
                                                css={styles.inputField}
                                            />
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                        <Button
                            css={styles.button}
                            color="primary"
                            type="submit"
                            id="submit-button"
                            loading={requestingOtp || verifyingOtp}
                            variant="outlined"
                            fullWidth
                        >
                            {step === OtpStep.EMAIL
                                ? t('supabase_otp_login_email_submit')
                                : t('supabase_otp_login_otp_verify_submit')}
                        </Button>
                        {step === OtpStep.EMAIL && (
                            <Box css={styles.loginTypeSwitchWrap}>
                                <Typography
                                    variant="body2"
                                    css={styles.secondaryButton}
                                    onClick={switchToPassword}
                                    id="switch-to-password-button"
                                >
                                    {t('supabase_otp_login_switch_to_password')}
                                </Typography>
                            </Box>
                        )}
                        {step === OtpStep.TOKEN && (
                            <Box css={styles.loginRetryOtpWrap}>
                                <Typography variant="body2">
                                    {t('supabase_otp_login_otp_verify_code_not_received')}
                                </Typography>
                                <Button
                                    onClick={handleResendCode}
                                    css={styles.resendCodeButton}
                                    loading={requestingOtp}
                                    disabled={resendCountdown > 0}
                                >
                                    {resendCountdown > 0
                                        ? `${t('supabase_otp_login_otp_verify_code_switch_to_email')} (in ${resendCountdown} seconds)`
                                        : t('supabase_otp_login_otp_verify_code_switch_to_email')}
                                </Button>
                            </Box>
                        )}
                    </form>
                    <Box>
                        {step === OtpStep.TOKEN && (
                            <Box css={styles.loginTypeSwitchWrap}>
                                <Typography
                                    variant="body2"
                                    role="button"
                                    css={styles.secondaryButton}
                                    tabIndex={0}
                                    onClick={() => {
                                        setToken(Array(6).fill(''));
                                        resetVerifyOtp();
                                        resetOtpRequest();
                                        goToPreviousStep();
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setToken(Array(6).fill(''));
                                            resetVerifyOtp();
                                            resetOtpRequest();
                                            goToPreviousStep();
                                        }
                                    }}
                                >
                                    {t('supabase_otp_login_otp_verify_code_change_email')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
};
